'use client';

import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { LinkSquare02Icon } from '@terra/ui-icons';
import {
  LabelDefault,
  MetaDefault,
} from '../../../../../components/typography';
import type { CardRenderContext } from '../../tool-config-types';
import type { ParsedWebPage } from '../../ToolCallsRenderers/types';
import { InlineExecutionToolCard } from '../shared/inline-execution-tool-card';
import { getHITLArtifactHeaderActions } from '../shared/hitl-artifact-header-actions';
import { formatCountedToolCallLabel } from '../shared/format-counted-tool-call-label';
import { EndpointsFlatList } from '../endpoints/endpoints-card-renderer';
import { parseAssetsListContent } from './assets-list-parser';

const ENDPOINTS_SECTION_LABEL = 'API Endpoints';
const WEB_PAGES_SECTION_LABEL = 'Web Pages';

function WebPagesFlatList({
  webPages,
  onOpenObject,
}: {
  webPages: ParsedWebPage[];
  onOpenObject?: CardRenderContext['onOpenObject'];
}) {
  return (
    <VStack alignItems="stretch" gap={1.25} width="100%">
      {webPages.map((webPage, index) => {
        const previewText = webPage.url || 'Web page';
        const webPageId = webPage.id;
        const canOpen = Boolean(onOpenObject && webPageId);

        return (
          <HStack
            key={webPageId ?? `web-page-${index}`}
            gap={1.5}
            alignItems="center"
            cursor={canOpen ? 'pointer' : 'default'}
            _hover={canOpen ? { color: 'fg.default' } : undefined}
            onClick={
              canOpen && webPageId
                ? () =>
                    onOpenObject?.({
                      type: 'webpage',
                      id: webPageId,
                      label: previewText,
                    })
                : undefined
            }
          >
            <Box flexShrink={0}>
              <LinkSquare02Icon size={10} />
            </Box>
            <MetaDefault color="fg.default" truncate flex={1}>
              {previewText}
            </MetaDefault>
          </HStack>
        );
      })}
    </VStack>
  );
}

export function renderAssetsListCard(ctx: CardRenderContext) {
  const isRunning =
    ctx.toolCallData.status === 'started' ||
    ctx.toolCallData.status === 'in-progress';
  const parsed = parseAssetsListContent(ctx.toolCallData.output);
  const endpoints = parsed?.endpoints ?? [];
  const webPages = parsed?.webPages ?? [];
  const total = endpoints.length + webPages.length;
  const showSectionLabels = endpoints.length > 0 && webPages.length > 0;

  if (
    !isRunning &&
    total === 0 &&
    !ctx.toolCallData.error &&
    ctx.toolCallData.status === 'completed'
  ) {
    return null;
  }

  const displayText = isRunning
    ? ctx.labelPresent
    : total > 0
      ? formatCountedToolCallLabel({
          label: ctx.labelPast,
          count: total,
        })
      : ctx.labelPast;

  return (
    <InlineExecutionToolCard
      title={displayText}
      status={ctx.toolCallData.status}
      error={ctx.toolCallData.error}
      headerActions={getHITLArtifactHeaderActions(ctx)}
      defaultExpanded={false}
      autoCollapseWhenComplete={ctx.autoCollapseWhenComplete}
      forceExpanded={ctx.forceExpanded}
    >
      {total > 0 ? (
        <VStack alignItems="stretch" gap={2} width="100%" overflowX="hidden">
          {endpoints.length > 0 ? (
            <VStack alignItems="stretch" gap={1.25} width="100%">
              {showSectionLabels ? (
                <LabelDefault color="fg.muted">
                  {ENDPOINTS_SECTION_LABEL}
                </LabelDefault>
              ) : null}
              <EndpointsFlatList
                endpoints={endpoints}
                onOpenObject={ctx.onOpenObject}
              />
            </VStack>
          ) : null}
          {webPages.length > 0 ? (
            <VStack alignItems="stretch" gap={1.25} width="100%">
              {showSectionLabels ? (
                <LabelDefault color="fg.muted">
                  {WEB_PAGES_SECTION_LABEL}
                </LabelDefault>
              ) : null}
              <WebPagesFlatList
                webPages={webPages}
                onOpenObject={ctx.onOpenObject}
              />
            </VStack>
          ) : null}
        </VStack>
      ) : null}
    </InlineExecutionToolCard>
  );
}
